'use client';

/**
 * Bento-grid testimonial wall. A single-row title sits on the left with
 * prev/next scroll arrows on the right (the LearningJourneys header
 * pattern); below it a horizontally-scrollable bento of review cards.
 * Each card stacks the star rating and the comment, then closes with an
 * identity row — the avatar beside the author name over their role.
 *
 * <p>The bento is built explicitly: items are grouped into columns —
 * a "featured" item becomes a tall card that fills a whole column,
 * the rest pair up two-high in a stacked column. When the authored
 * data marks nothing featured, every third item is promoted so the
 * tall | stacked-pair | tall rhythm of the BTK "Kullanıcılarımız Ne
 * Diyor?" reference always forms. The columns live in a flex rail
 * (`flex: 1 0 300px`) so up to three fill the width and any extra
 * scroll — the header arrows page through them. No CSS-grid
 * auto-placement, so it can't collapse to a flat row inside the
 * portal. Dark mode via {@code [data-theme="dark"]}/{@code .dark};
 * hover lift unless {@code prefers-reduced-motion}.
 */
import { useRef, useState, type CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlockPart } from '@akademi/site-builder-runtime';
import { Card } from '@akademi/ui';
import { tx, asList, asNumber } from '../_shared/coerce';
import { sectionInner, sectionPad, sectionTitle } from '../_shared/styles';
import { resolvePaddingStyle, type PaddingBox } from '../_shared/resolvePadding';
import { EmptyState } from '../_shared/EmptyState';
import { HeadingLine } from '../_shared/HeadingLine';
import { BlockRoot } from '../_shared/BlockRoot';

type Localized = string | Record<string, string>;

interface BentoTestimonial {
  quote: Localized;
  author: Localized;
  role?: Localized;
  avatar?: string;
  /** 0–5; rounded for the star row. */
  rating?: number;
}

export interface TestimonialsBentoProps {
  title?: Localized;
  /** Default star rating used when an item omits its own. */
  maxRating?: number;
  /** Mobile-only "show more" label (collapsed state). */
  moreLabel?: Localized;
  /** Mobile-only "show less" label (expanded state). */
  lessLabel?: Localized;
  items?: BentoTestimonial[];
  /** Draw the decorative gradient rule beside the section heading. */
  headingLine?: boolean;
  style?: CSSProperties;
  paddingOuter?: PaddingBox;
  paddingInner?: PaddingBox;
}

/** How many cards are visible on phones before "show more" is tapped. */
const MOBILE_LIMIT = 3;

type CardVariant = 'tall' | 'compact';
interface Placed {
  t: BentoTestimonial;
  i: number;
}
interface Column {
  type: 'tall' | 'pair';
  items: Placed[];
}

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

/**
 * Group testimonials into bento columns automatically — no per-item
 * authoring. Every third item (index 0, 3, 6, …) becomes a tall card
 * filling its own column; the two between each pair up into a stacked
 * column, giving the tall | pair | tall cadence of the reference for
 * any number of testimonials.
 */
function toColumns(list: BentoTestimonial[]): Column[] {
  const columns: Column[] = [];
  let pair: Placed[] = [];
  const flush = () => {
    if (pair.length) {
      columns.push({ type: 'pair', items: pair });
      pair = [];
    }
  };
  list.forEach((t, i) => {
    if (i % 3 === 0) {
      flush();
      columns.push({ type: 'tall', items: [{ t, i }] });
    } else {
      pair.push({ t, i });
      if (pair.length === 2) flush();
    }
  });
  flush();
  return columns;
}

function StarRow({ rating, max }: { rating: number; max: number }) {
  const filled = Math.max(0, Math.min(max, Math.round(rating)));
  return (
    <div className="tb-stars" role="img" aria-label={`${filled} / ${max}`}>
      {Array.from({ length: max }, (_, i) => (
        <svg
          key={i}
          className={i < filled ? 'tb-star tb-star-on' : 'tb-star'}
          viewBox="0 0 24 24"
          width={19}
          height={19}
          aria-hidden="true"
        >
          <path d="M12 2.5l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.77l-5.8 3.05 1.1-6.46-4.69-4.58 6.49-.94L12 2.5z" />
        </svg>
      ))}
    </div>
  );
}

export function TestimonialsBento({
  title,
  maxRating = 5,
  moreLabel,
  lessLabel,
  items,
  headingLine,
  style,
  paddingOuter,
  paddingInner,
}: TestimonialsBentoProps) {
  const { t } = useTranslation('site-builder-blocks');
  const part = useBlockPart();
  const heading = title ?? t('testimonialsBento.title');
  const more = moreLabel ?? t('shared.showMore');
  const less = lessLabel ?? t('shared.showLess');
  const list = asList<BentoTestimonial>(items);
  const max = Math.max(1, Math.min(5, asNumber(maxRating, 5)));
  const columns = toColumns(list);
  const scrollRef = useRef<HTMLDivElement>(null);
  // Mobile-only: reveal cards MOBILE_LIMIT at a time behind a "show more"
  // button — each tap shows 3 more; once all are visible the button
  // collapses back to the first 3 (desktop/tablet scroll instead, so CSS
  // ignores this state above the phone breakpoint). SSR and the client
  // both start at MOBILE_LIMIT — no hydration mismatch.
  const [visibleCount, setVisibleCount] = useState(MOBILE_LIMIT);
  const hasMore = list.length > MOBILE_LIMIT;
  const fullyShown = visibleCount >= list.length;

  function scroll(dir: 'prev' | 'next') {
    const el = scrollRef.current;
    if (!el) return;
    const amount = el.clientWidth * 0.9;
    el.scrollBy({ left: dir === 'next' ? amount : -amount, behavior: 'smooth' });
  }

  return (
    <BlockRoot
      as="section"
      className="tb-section"
      aria-labelledby="tb-title"
      style={{ ...sectionPad, ...resolvePaddingStyle(paddingOuter), ...style }}
    >
      <div style={{ ...sectionInner, ...resolvePaddingStyle(paddingInner) }}>
        <div className="tb-header" {...part('header')}>
          <h2 id="tb-title" style={sectionTitle} className="tb-heading" {...part('title')}>
            {tx(heading)}
          </h2>
          {headingLine && <HeadingLine />}
          {list.length > 0 && (
            <div className="tb-nav">
              <button
                type="button"
                className="tb-arrow"
                aria-label={t('shared.nav.previous')}
                onClick={() => scroll('prev')}
                {...part('pager', 0)}
              >
                <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden="true">
                  <path
                    d="M15 18l-6-6 6-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                type="button"
                className="tb-arrow"
                aria-label={t('shared.nav.next')}
                onClick={() => scroll('next')}
                {...part('pager', 1)}
              >
                <svg viewBox="0 0 24 24" width={18} height={18} aria-hidden="true">
                  <path
                    d="M9 18l6-6-6-6"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          )}
        </div>

        {list.length === 0 ? (
          <div className="tb-empty" {...part('empty')}>
            <EmptyState title={t('testimonials.empty.title')} hint={t('testimonials.empty.hint')} />
          </div>
        ) : (
          <>
            <div className="tb-rail" ref={scrollRef} {...part('list')}>
              {columns.map((col, ci) => {
                // A column whose first card sits past the current visible
                // count is hidden on phones until "show more" reveals it.
                // (Columns never straddle the count — pairs sit between
                // tall cards, which fall on multiples of 3 — so each +3
                // reveals exactly the next tall+pair group.)
                const extra = (col.items[0]?.i ?? 0) >= visibleCount;
                const base = col.type === 'tall' ? 'tb-col tb-col-tall' : 'tb-col';
                return (
                  <div key={ci} className={extra ? `${base} tb-col-extra` : base}>
                    {col.items.map(({ t, i }) => (
                      <BentoCard
                        key={i}
                        testimonial={t}
                        max={max}
                        variant={col.type === 'tall' ? 'tall' : 'compact'}
                      />
                    ))}
                  </div>
                );
              })}
            </div>

            {hasMore && (
              <button
                type="button"
                className="tb-more"
                aria-expanded={fullyShown}
                onClick={() =>
                  setVisibleCount((c) => (c >= list.length ? MOBILE_LIMIT : c + MOBILE_LIMIT))
                }
                {...part('pager', 2)}
              >
                {tx(fullyShown ? less : more)}
              </button>
            )}
          </>
        )}
      </div>

      <style dangerouslySetInnerHTML={{ __html: bentoCss }} />
    </BlockRoot>
  );
}

function BentoCard({
  testimonial,
  max,
  variant,
}: {
  testimonial: BentoTestimonial;
  max: number;
  variant: CardVariant;
}) {
  const quote = tx(testimonial.quote);
  const author = tx(testimonial.author);
  const role = testimonial.role ? tx(testimonial.role) : '';
  const initials = getInitials(author);
  const rating = asNumber(testimonial.rating, max);

  // Reading order inside a card: rating and comment stack in a column,
  // then the identity row — photo on the left, name over role beside it.
  return (
    <Card asChild>
      <article className={`tb-card tb-card-${variant}`}>
        <StarRow rating={rating} max={max} />
        <p className="tb-quote">{quote}</p>

        <div className="tb-person">
          <div className="tb-avatar" aria-hidden="true">
            {testimonial.avatar ? (
              <img
                src={testimonial.avatar}
                alt=""
                loading="lazy"
                decoding="async"
                width={56}
                height={56}
              />
            ) : (
              <span>{initials}</span>
            )}
          </div>
          <div className="tb-identity">
            <cite className="tb-author">{author}</cite>
            {role && <div className="tb-role">{role}</div>}
          </div>
        </div>
      </article>
    </Card>
  );
}

const bentoCss = `
.tb-section { background: transparent; }
.tb-header {
  display: flex; align-items: center;  
  gap: var(--space-5, 20px);
  margin-bottom: var(--space-8, 32px);
}
.tb-heading { white-space: nowrap; }
.tb-nav { display: inline-flex; gap: var(--space-2, 8px); flex-shrink: 0; }
.tb-arrow {
  display: inline-flex; align-items: center; justify-content: center;
  width: 40px; height: 40px;
  border-radius: var(--radius-pill, 9999px);
  border: 1px solid var(--color-border-default, rgba(120, 120, 140, 0.28));
  background: var(--color-surface-muted, #ffffff);
  color: var(--color-text-primary, #18181b);
  cursor: pointer;
  transition: background 160ms ease, border-color 160ms ease, color 160ms ease;
}
.tb-arrow:hover {
  border-color: var(--color-brand-500, #3b82f6);
  color: var(--color-brand-500, #3b82f6);
}
.tb-arrow:focus-visible {
  outline: 2px solid var(--color-brand-500, #3b82f6);
  outline-offset: 2px;
}
.tb-empty { margin-top: var(--space-6, 24px); }
/* Bento rail: flex columns at a FIXED one-third width (no grow, no
   shrink). Three columns fill the row exactly (the static reference
   bento); a fourth and beyond keep the same width and overflow, so the
   rail scrolls and the header arrows page through it — card widths never
   shrink as testimonials are added. */
.tb-rail {
  display: flex;
  align-items: stretch;
  gap: var(--space-5, 20px);
  overflow-x: auto;
  scroll-snap-type: x proximity;
  scrollbar-width: none;
  -ms-overflow-style: none;
  padding-bottom: 4px;
}
.tb-rail::-webkit-scrollbar { display: none; }
.tb-col {
  box-sizing: border-box;
  flex: 0 0 calc((100% - 2 * var(--space-5, 20px)) / 3);
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: var(--space-5, 20px);
  scroll-snap-align: start;
}
/* Rendered as <Card>: border + elevation come from the component
   (ring-(--color-border-brand) + shadow-(--shadow-brand)). */
.tb-card {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: var(--space-3, 12px);
  padding: var(--space-5, 20px);
  border-radius: var(--radius-lg, 16px);
  background: var(--color-surface-muted, #fafafa);
  color: var(--color-text-primary, #18181b);
  transition: transform 180ms ease;
}
.tb-card:hover {
  transform: translateY(-3px);
}
/* Tall card = the big tile: fills its column so the bento silhouette holds,
   and its content (stars, quote, identity row) rides as one block in the
   vertical centre. The column's height comes from the stacked pair beside
   it, not from this card's text, so a short featured quote leaves slack —
   centring splits it evenly above and below instead of pooling it at one
   end (top-hugged looked bottom-heavy; pinning the identity row to the
   bottom with margin-top:auto punched a hole through the middle). */
.tb-card-tall { flex: 1; justify-content: center; }
/* Identity row: photo left, name over role beside it. */
.tb-person {
  display: flex;
  align-items: center;
  gap: var(--space-3, 12px);
  padding-top: var(--space-1, 4px);
}
.tb-identity { min-width: 0; display: flex; flex-direction: column; }
.tb-avatar {
  flex: 0 0 auto; width: 72px; height: 72px;
  border-radius: var(--radius-pill, 9999px);
  overflow: hidden;
  display: grid; place-items: center;
  background: linear-gradient(145deg,
    var(--color-surface-elevated, #f4f4f5),
    var(--color-surface-muted, #e4e4e7));
  border: 1px solid var(--color-border-subtle, rgba(120, 120, 140, 0.2));
}
.tb-card-compact .tb-avatar { width: 48px; height: 48px; }
.tb-avatar img { width: 100%; height: 100%; object-fit: cover; }
.tb-avatar span {
  font-family: var(--font-family-display, inherit);
  font-size: var(--font-size-md, 16px);
  font-weight: 700;
  letter-spacing: var(--font-tracking-snug, -0.02em);
}
.tb-stars { display: inline-flex; gap: 2px; }
.tb-star { fill: var(--color-border-strong, #d4d4d8); }
/* Filled stars use the canonical --color-stars token (same as the shared
   Stars component behind the course cards) so ratings match site-wide. */
.tb-star-on { fill: var(--color-stars, #EF8735); }
.tb-author {
  font-family: var(--font-family-display, inherit);
  font-style: normal;
  font-size: var(--font-size-md, 16px);
  font-weight: 700;
  line-height: 1.25;
  color: var(--color-text-primary, #18181b);
}
.tb-role {
  margin-top: 2px;
  color: var(--color-text-secondary, #71717a);
  font-size: var(--font-size-xs, 12px);
}
.tb-quote {
  margin: 0;
  font-family: var(--font-family-body, inherit);
  color: var(--color-text-secondary, #52525b);
  font-size: var(--font-size-sm, 14px);
  line-height: 1.55;
}
.tb-card-tall .tb-quote { color: var(--color-text-primary, #18181b); }
/* "Show more" is a phones-only affordance (desktop/tablet scroll). */
.tb-more { display: none; }
:where([data-theme="dark"], .dark) .tb-arrow {
  background: var(--color-surface-card, #18181b);
  border-color: var(--color-border-default, rgba(255, 255, 255, 0.16));
  color: var(--color-text-primary, #fafafa);
}
/* Keep the section transparent in dark mode too — only the cards carry a
   surface so the block inherits the page background like it does in light. */
:where([data-theme="dark"], .dark) .tb-section {
  background: transparent;
}
:where([data-theme="dark"], .dark) .tb-card {
  background: var(--color-surface-elevated, #27272a);
}
:where([data-theme="dark"], .dark) .tb-avatar {
  background: linear-gradient(145deg,
    var(--color-surface-elevated, #27272a),
    var(--color-surface-muted, #18181b));
  border-color: var(--color-border-subtle, rgba(255, 255, 255, 0.1));
}
/* Dark mode: dim only the empty stars. The :not() keeps filled stars on the
   base --color-stars fill, which is theme-independent (matching the shared
   Stars component), so they need no dark override. */
:where([data-theme="dark"], .dark) .tb-star:not(.tb-star-on) {
  fill: rgba(255, 255, 255, 0.18);
}
@media (max-width: 900px) {
  /* Two fixed columns per view on tablet. */
  .tb-col { flex: 0 0 calc((100% - var(--space-5, 20px)) / 2); }
}
@media (max-width: 600px) {
  /* Phones: drop the horizontal rail and bento entirely — a plain block
     flow stacks every card full-width in reading order, so nothing
     overflows the viewport. (block flow, not column-flex, avoids the
     flex cross-axis sizing quirks that can push cards past the screen.)
     The scroll arrows are meaningless here, so hide them. */
  .tb-rail { display: block; overflow: visible; }
  .tb-col { width: auto; }
  .tb-col + .tb-col { margin-top: var(--space-5, 20px); }
  .tb-card-tall { flex: 0 0 auto; }
  .tb-card-tall .tb-avatar { width: 48px; height: 48px; }
  .tb-nav { display: none; }
  .tb-header { margin-bottom: var(--space-6, 24px); }
  .tb-heading { white-space: normal; }
  /* Columns past the current visible count are hidden until "show more"
     reveals them (3 at a time). Hidden columns also drop their stacking
     margin so there's no gap before the button. */
  .tb-col-extra { display: none; }
  .tb-col-extra + .tb-col-extra { margin-top: 0; }
  .tb-more {
    display: inline-flex; align-items: center; justify-content: center;
    margin-top: var(--space-6, 24px);
    padding: var(--space-3, 12px) var(--space-6, 24px);
    width: 100%;
    border-radius: var(--radius-pill, 9999px);
    border: 1px solid var(--color-border-default, rgba(120, 120, 140, 0.28));
    background: var(--color-surface-card, #ffffff);
    color: var(--color-text-primary, #18181b);
    font-family: var(--font-family-display, inherit);
    font-size: var(--font-size-sm, 14px);
    font-weight: 600;
    cursor: pointer;
  }
  .tb-more:active { transform: translateY(1px); }
}
:where([data-theme="dark"], .dark) .tb-more {
  background: var(--color-surface-card, #18181b);
  border-color: var(--color-border-default, rgba(255, 255, 255, 0.16));
  color: var(--color-text-primary, #fafafa);
}
@media (hover: none) {
  .tb-card:hover { transform: none; }
}
@media (prefers-reduced-motion: reduce) {
  .tb-card { transition: none !important; }
  .tb-card:hover { transform: none !important; }
}
`;
